import moment from 'moment';
import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import Box from '../../../../components/Box';
import ColumnChooser from '../../../../components/ColumnChooser';
import CustomMultiSelect from '../../../../components/CustomMultiSelect';
import CustomSingleSelect from '../../../../components/CustomSingleSelect';
import CustomSwitch from '../../../../components/CustomSwitch';
import ExcelExportMenu from '../../../../components/ExcelExportMenu';
import { ExcelExportMode } from '../../../../helpers/excelExportRequest';
import Filters from '../../../../components/Filters';
import { ColumnChooserModel } from '../../../../models/common/ColumnChooserModel';
import { GetWillInvoicedListPagingAndFilterRequest } from '../../../../models/request/invoice/GetWillInvoicedListPagingAndFilterRequest';
import { RootState } from '../../../../redux/store';
import { ConstantService } from '../../../../services/ConstantService';
import { MenuComponent } from '../../../../_metronic/assets/ts/components';
import TableActions from '../../../../components/TableActions';
import { ServiceService } from '../../../../services/ServiceService';
import { CompanyService } from '../../../../services/CompanyService';
import CustomTextInput from '../../../../components/CustomTextInput';
import TableConstantActions from '../../../../components/TableConstantActions';

interface Props {
    columns: ColumnChooserModel[]
    setColumns: React.Dispatch<React.SetStateAction<ColumnChooserModel[]>>
    getColumns: () => ColumnChooserModel[]
    pageName: string
    pageVersion: string
    excelExport: (mode: ExcelExportMode) => void
    filter: GetWillInvoicedListPagingAndFilterRequest
    getData: (_filter?: GetWillInvoicedListPagingAndFilterRequest | undefined) => Promise<void>
    setFilter: React.Dispatch<React.SetStateAction<GetWillInvoicedListPagingAndFilterRequest>>
    selectedCount: number
    createInvoice: (isSale: boolean) => Promise<void>
    onSearchSubmit: (e: any) => void
}

const WillInvoicedListFilters = ({
    columns,
    setColumns,
    getColumns,
    pageName,
    pageVersion,
    excelExport,
    filter,
    getData,
    setFilter,
    selectedCount,
    createInvoice,
    onSearchSubmit
}: Props) => {
    const navigate = useNavigate();
    const auth = useSelector((state: RootState) => state.auth)

    useEffect(() => {
        setTimeout(() => { MenuComponent.reinitialization() }, 1000)
    }, [])

    return (
        <React.Fragment>
            <TableConstantActions
                searchOnly
                onSearchSubmit={onSearchSubmit}
                pageName={pageName}
                hideSearch={true}
                searchTerm={filter.searchTerm}
                selectedCount={selectedCount}
                setSearchTerm={(e) => setFilter({ ...filter, searchTerm: e })}
            />

            <TableActions
                list={[
                    { isDisabled: !filter.isIncome || selectedCount == 0, text: 'Satış Faturası Oluştur', onClick: () => createInvoice(true) },
                    { isDisabled: filter.isIncome || selectedCount == 0, text: 'Alış Faturası Oluştur', onClick: () => createInvoice(false) },
                ]}
            />

            <ColumnChooser
                columns={columns}
                getColumns={getColumns}
                pageName={pageName}
                pageVersion={pageVersion}
                setColumns={setColumns}
            />

            <ExcelExportMenu
                pageName={pageName}
                selectedCount={selectedCount}
                onExport={excelExport}
            />

            <Filters
                filter={filter}
                getData={getData}
                onReset={() => getData({
                    ...filter,
                    currentPage: 1,
                    skip: 0,
                    refNo: '',
                    selectedCustomers: [],
                    selectedSuppliers: [],
                    selectedReceiverFirms: [],
                    orderDateStart: moment(new Date()).subtract(1, 'month').format("YYYY-MM-DD"),
                    orderDateEnd: moment(new Date()).format("YYYY-MM-DD"),
                    loadingDateStart: '',
                    loadingDateEnd: '',
                    selectedOrderStatuses: [],
                    selectedOperationTypes: [],
                    selectedModule: undefined,
                    selectedDivisions: [],
                    selectedPos: [],
                    poNo: undefined,
                    donemYil: undefined,
                    donemAy: undefined
                })}
            >
                <div className='mb-5'>
                    <label className='form-label fw-bold'>Modül:</label>

                    <div>
                        <CustomSingleSelect
                            onChange={(e) => setFilter({
                                ...filter,
                                selectedModule: e
                            })}
                            selectedValue={filter.selectedModule}
                            onMenuOpen={() => ConstantService.getConstantList('MODULE')}
                            labelName='value'
                            valueName='constantId'
                            additionalValue1Name='additionalValue1'
                        />
                    </div>
                </div>

                {/* Ref No, Müşteri, Tedarikçi ve Alıcı Firma kolon başlığının altındaki
                    filtreye taşındı (22.09.2026). Burada yalnız kolonu olmayan alanlar kaldı. */}

                <div className='mb-5'>
                    <label className='form-label fw-bold'>Sipariş Tarihi:</label>

                    <Box gap='15px' flexDirection='row'>
                        <Box flex={1} justifyContent='center'>
                            <input
                                className="form-control form-control-solid"
                                type={'date'}
                                value={filter.orderDateStart}
                                onChange={(e) => setFilter({
                                    ...filter,
                                    orderDateStart: e.target.value
                                })}
                            />
                        </Box>
                        <Box flex={1} justifyContent='center'>
                            <input
                                className="form-control form-control-solid"
                                type={'date'}
                                value={filter.orderDateEnd}
                                onChange={(e) => setFilter({
                                    ...filter,
                                    orderDateEnd: e.target.value
                                })}
                            />
                        </Box>
                    </Box>
                </div>

                {/* Müşteri panele geri geldi (05.10.2026): yalnız "Müşteri" işaretli firmalar, tek seçim. Kolon
                    başlığının altındaki Müşteri filtresi de çalışmaya devam eder (ikisi birlikte uygulanır). */}
                <div className='mb-5'>
                    <label className='form-label fw-bold'>Müşteri:</label>

                    <div>
                        <CustomSingleSelect
                            onChange={(e) => setFilter({
                                ...filter,
                                selectedCustomers: e ? [e] : []
                            })}
                            selectedValue={filter.selectedCustomers?.[0]}
                            onMenuOpen={() => CompanyService.getCompanyList({ isCustomer: true })}
                            labelName='name'
                            valueName='companyId'
                        />
                    </div>
                </div>

                <div className='mb-5'>
                    <label className='form-label fw-bold'>Yükleme Tarihi:</label>

                    <Box gap='15px' flexDirection='row'>
                        <Box flex={1} justifyContent='center'>
                            <input
                                className="form-control form-control-solid"
                                type={'date'}
                                value={filter.loadingDateStart}
                                onChange={(e) => setFilter({
                                    ...filter,
                                    loadingDateStart: e.target.value
                                })}
                            />
                        </Box>
                        <Box flex={1} justifyContent='center'>
                            <input
                                className="form-control form-control-solid"
                                type={'date'}
                                value={filter.loadingDateEnd}
                                onChange={(e) => setFilter({
                                    ...filter,
                                    loadingDateEnd: e.target.value
                                })}
                            />
                        </Box>
                    </Box>
                </div>

                <div className='mb-5'>
                    <label className='form-label fw-bold'>Sipariş Durumu:</label>

                    <div>
                        <CustomMultiSelect
                            onChange={(e) => setFilter({
                                ...filter,
                                selectedOrderStatuses: e
                            })}
                            selectedValues={filter.selectedOrderStatuses}
                            onMenuOpen={() => ConstantService.getConstantList('ORDER_STATUS')}
                            labelName='value'
                            valueName='constantId'
                        />
                    </div>
                </div>

                <div className='mb-5'>
                    <label className='form-label fw-bold'>Operasyon Türü:</label>

                    <div>
                        <CustomMultiSelect
                            onChange={(e) => setFilter({
                                ...filter,
                                selectedOperationTypes: e
                            })}
                            selectedValues={filter.selectedOperationTypes}
                            onMenuOpen={() => ConstantService.getConstantList('OPERATION_TYPE')}
                            labelName='value'
                            valueName='constantId'
                        />
                    </div>
                </div>

                <div className='mb-5'>
                    <label className='form-label fw-bold'>Bölüm:</label>

                    <div>
                        <CustomMultiSelect
                            onChange={(e) => setFilter({
                                ...filter,
                                selectedDivisions: e
                            })}
                            selectedValues={filter.selectedDivisions ?? []}
                            onMenuOpen={() => ConstantService.getConstantList('DIVISION')}
                            labelName='value'
                            valueName="constantId"
                        />
                    </div>
                </div>

                <div className='mb-5'>
                    <label className='form-label fw-bold'>PO:</label>

                    <div>
                        <CustomMultiSelect
                            onChange={(e) => setFilter({
                                ...filter,
                                selectedPos: e
                            })}
                            selectedValues={filter.selectedPos ?? []}
                            onMenuOpen={() => ServiceService.getServiceList(true)}
                            labelName='name'
                            valueName='serviceId'
                            additionalValue1Name='incomePercentageName'
                        />
                    </div>
                </div>

                <div className='mb-5'>
                    <label className='form-label fw-bold'>Dönem (aylık sabit gelir):</label>
                    <input
                        type='month'
                        className='form-control form-control-solid'
                        value={filter.donemYil && filter.donemAy ? `${filter.donemYil}-${String(filter.donemAy).padStart(2, '0')}` : ''}
                        onChange={(e) => {
                            const [y, a] = e.target.value ? e.target.value.split('-').map(Number) : [undefined, undefined];
                            // Dönem seçilince sipariş tarihi aralığı temizlenir; dönem kendisi bir aralıktır.
                            setFilter({ ...filter, donemYil: y, donemAy: a, ...(y ? { orderDateStart: '', orderDateEnd: '' } : {}) })
                        }}
                    />
                    <div className='form-text'>Seçilirse yalnız o dönemin aylık sabit gelir (kira / personel) siparişleri listelenir.</div>
                </div>

                <div className='mb-5'>
                    <CustomTextInput
                        labelText='Po Numarası (birden fazla ise virgülle ayırın):'
                        placeholder='Örn. P26245641R, P26226042R'
                        value={filter?.poNo ?? ''}
                        setValue={(value: string) => {
                            setFilter({
                                ...filter,
                                poNo: value
                            })
                        }}
                    />
                </div>

                <div>
                    <Box flexDirection='row'>
                        <Box flex={1}>
                            <label className='form-label fw-bold'>Satış Faturası Olmayan</label>

                            <CustomSwitch isActive={filter.isIncome} setIsActive={() => setFilter({ ...filter, isIncome: true })} />
                        </Box>
                        <Box flex={1}>
                            <label className='form-label fw-bold'>Alış Faturası Olmayan</label>

                            <CustomSwitch isActive={!filter.isIncome} setIsActive={() => setFilter({ ...filter, isIncome: false })} />
                        </Box>
                    </Box>
                </div>
            </Filters>

            {/* <React.Fragment>
                {
                    auth.user.user?.data.authorizedMenus.find(x => x.menuName == pageName)?.canInsert && (
                        <div className='m-0'>
                            <a
                                onClick={() => navigate('/willInvoiced/listadd')}
                                className='btn btn-sm fw-bold btn-primary'
                            >
                                Yeni Araç Ekle
                            </a>
                        </div>
                    )
                }
            </React.Fragment> */}
        </React.Fragment>
    )
}

export default WillInvoicedListFilters;